import React from 'react';

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

export default function SummaryCards({ summary, isMobile }) {
  if (!summary) return null;

  const portfolioValue       = summary.portfolio_value      || 0;
  const collateralValue      = summary.collateral_value     || 0;
  const changePct            = summary.change_pct           || 0;
  const profitSinceInception = summary.profit_since_inception || 0;
  const colPct = portfolioValue > 0 ? Math.round((collateralValue / portfolioValue) * 100) : 0;

  if (isMobile) {
    return (
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
          Portfolio Summary
        </div>

        {/* Portfolio Value — full width on mobile */}
        <div style={{
          background:'#5A1020', borderRadius:10, padding:'14px 16px',
          color:'#fff', marginBottom:8, position:'relative', overflow:'hidden',
        }}>
          <div style={{ position:'absolute', right:-16, top:-16, width:80, height:80, borderRadius:'50%', background:'rgba(255,255,255,0.06)' }} />
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.65)', marginBottom:6 }}>Portfolio Value</div>
          <div style={{ fontSize:22, fontWeight:700, color:'#fff', marginBottom:6 }}>{fmt(portfolioValue)}</div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(255,255,255,0.15)', padding:'2px 10px', borderRadius:20, fontSize:10, color:'#fff' }}>
            {changePct >= 0 ? '↑' : '↓'} {Math.abs(changePct).toFixed(2)}% this period
          </div>
        </div>

        {/* 2-column grid for remaining cards */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
          <MobileCard label="Collateral Value" value={fmt(collateralValue)} sub={`${colPct}% of Market`} />
          <MobileCard label="Pledge Return" value={fmt(profitSinceInception)} sub="Since Inception" highlight />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
          <MobileCard label="XIRR" value={`${summary.xirr_pct?.toFixed(2) || 0}%`} />
          <MobileCard label="Winning Days" value={`${summary.winning_days || 0}/${summary.total_traded_days || 0}`} />
          <MobileCard label="Utilization" value={`${summary.utilization_pct}%`} />
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>
        Portfolio Summary
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
        <div style={{ background:'#5A1020', borderRadius:10, padding:'16px 18px', color:'#fff', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', right:-16, top:-16, width:90, height:90, borderRadius:'50%', background:'rgba(255,255,255,0.06)' }} />
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.65)', marginBottom:8, display:'flex', alignItems:'center', gap:4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
            Portfolio Value
          </div>
          <div style={{ fontSize:24, fontWeight:700, color:'#fff', marginBottom:8 }}>{fmt(portfolioValue)}</div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(255,255,255,0.15)', padding:'2px 10px', borderRadius:20, fontSize:10, color:'#fff' }}>
            {changePct >= 0 ? '↑' : '↓'} {Math.abs(changePct).toFixed(2)}% this period
          </div>
        </div>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'16px 18px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:8, display:'flex', alignItems:'center', gap:4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#6B1C2A" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span style={{ color:'#6B1C2A', fontWeight:600 }}>Collateral Value</span>
          </div>
          <div style={{ fontSize:22, fontWeight:700, color:'#333', marginBottom:6 }}>{fmt(collateralValue)}</div>
          <div style={{ fontSize:10, color:'#888' }}>{colPct}% of Market value</div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10 }}>
        <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>Pledge Return</div>
          <div style={{ fontSize:16, fontWeight:700, color:'#6B1C2A' }}>{fmt(profitSinceInception)}</div>
          <div style={{ fontSize:9, color:'#aaa', marginTop:3 }}>Since Inception</div>
        </div>
        <StatCard label="XIRR" value={`${summary.xirr_pct?.toFixed(2) || 0}%`} />
        <StatCard label="Winning Days" value={`${summary.winning_days || 0} / ${summary.total_traded_days || 0}`} />
        <StatCard label="Utilization %" value={`${summary.utilization_pct}%`} />
      </div>
    </div>
  );
}

function MobileCard({ label, value, sub, highlight }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:8, padding:'10px 12px' }}>
      <div style={{ fontSize:9, color:'#888', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:14, fontWeight:700, color: highlight ? '#6B1C2A' : '#333' }}>{value}</div>
      {sub && <div style={{ fontSize:9, color:'#aaa', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'12px 14px' }}>
      <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:700, color:'#333' }}>{value}</div>
    </div>
  );
}
